// @flow
import * as React from 'react';
import {BodyMono} from 'sail/Text';
import Group from 'sail/Group';
import {useAccount} from 'docs/data';
import {makeStripeRequest} from './resource';
import {Spinner} from '../cli/general';

const CliLink = ({href, children}) => (
  <a className="CliLink" href={href} target="_blank" rel="noreferrer">
    {children}
  </a>
);

// Make the request and get the response.
export const CliConnection = ({
  path,
  params,
  api_key,
  setAllowInput,
}: {
  path: string,
  params: {[string]: string},
  api_key: string,
  setAllowInput: (boolean) => void,
}) => {
  const {account} = useAccount();
  const [output, setOutput] = React.useState([]);
  const socket = React.useRef();
  const latestLogRef = React.useRef();

  function assembleOutput() {
    if (!socket.current) {
      return <Spinner text="Getting ready..." />;
    }
    return (
      <BodyMono color="gray200">
        <span>Ready! (^C to quit)</span>
        {output.map((line, idx) => {
          let type_url = `${account.hosts['dashboard.stripe.com']}/test/events?type=${line.type}`;
          let event_url = `${account.hosts['dashboard.stripe.com']}/test/events/${line.id}`;

          if (line.account) {
            type_url = `${account.hosts['dashboard.stripe.com']}/${line.account}/test/events?type=${line.type}`;
            event_url = `${account.hosts['dashboard.stripe.com']}/${line.account}/test/events/${line.id}`;
          }
          return (
            <div
              key={idx}
              ref={idx === output.length - 1 ? latestLogRef : null}
            >
              <Group direction="horizontal" spacing={12}>
                <BodyMono color="gray400" wrap={false}>
                  {line.datetime}
                </BodyMono>
                <span>&rarr;</span>
                {line.account && <span>connect</span>}
                <Group direction="horizontal" spacing={4}>
                  <CliLink href={type_url}>{line.type}</CliLink>
                  <span>
                    {'['}
                    <CliLink href={event_url}>{line.id}</CliLink>
                    {']'}
                  </span>
                </Group>
              </Group>
            </div>
          );
        })}
      </BodyMono>
    );
  }

  // Scroll to the latest log when new output is rendered
  React.useEffect(() => {
    if (latestLogRef?.current) {
      latestLogRef.current.scrollIntoView({block: 'nearest', inline: 'start'});
    }
  }, [output]);

  React.useEffect(() => {
    // Close the socket when the page closes or refreshes
    function closeSocket(componentIsMounted = true) {
      document.removeEventListener('keydown', onkeydown);
      if (socket.current) {
        socket.current.close();
        if (componentIsMounted) {
          setAllowInput(true);
        }
      }
    }

    function onkeydown(event: KeyboardEvent) {
      const ctrl = event.ctrlKey ? event.ctrlKey : event.key === 'Control';
      const shift = event.shiftKey ? event.shiftKey : event.key === 'Shift';

      if ((event.key === 'c' && ctrl) || (event.key === 'c' && ctrl && shift)) {
        closeSocket();
      }
    }
    document.addEventListener('keydown', onkeydown);
    return () => {
      closeSocket(false);
    };
  }, [setAllowInput]);

  function padNumber(num: number): string {
    if (num < 10) {
      return `0${String(num)}`;
    }
    return String(num);
  }

  React.useEffect(() => {
    function handleMessage(e) {
      const msg = JSON.parse(String(e.data));

      if (
        ((msg.endpoint.api_version === null && !('latest' in params)) ||
          (msg.endpoint.api_version !== null && 'latest' in params)) &&
        msg.event_payload
      ) {
        const currentdate = new Date();

        const datetime = `${padNumber(currentdate.getFullYear())}-${padNumber(
          currentdate.getMonth() + 1,
        )}-${padNumber(currentdate.getDate())} ${padNumber(
          currentdate.getHours(),
        )}:${padNumber(currentdate.getMinutes())}:${padNumber(
          currentdate.getSeconds(),
        )}`;
        const payload = JSON.parse(msg.event_payload);

        setOutput((state) => [
          ...state,
          {
            datetime,
            type: payload.type,
            id: payload.id,
            account: payload.account,
          },
        ]);
      }
    }

    const buildSocket = async () => {
      const authorize_params = {
        device_name: 'stripe_clui',
        websocket_feature: 'webhooks',
      };

      const response = await makeStripeRequest({
        url: `${account.hosts['api.stripe.com']}/v1/stripecli/sessions`,
        method: 'post',
        api_key,
        raw_params: authorize_params,
        additional_headers: {
          'X-Stripe-Client-User-Agent': 'Stripe/v1 stripe-clui',
        },
      });

      try {
        await fetch(`${response['websocket_url'].slice(4)}`, {
          method: 'GET',
          mode: 'no-cors',
        });
      } catch (e) {}

      const websocket_id = response['websocket_id'];
      const websocket_url = `${response['websocket_url']}?x_stripe_client_user_agent=Stripe/v1%20stripe-clui&http_websocket_id=${websocket_id}`;
      const web_socket = new WebSocket(websocket_url);
      socket.current = web_socket;
      setOutput([]);

      if (web_socket) {
        web_socket.onmessage = handleMessage;
      }
    };
    buildSocket();
  }, [api_key, params, account.hosts]);

  return assembleOutput();
};
